import { getApiClient } from '@apps/web/lib/api_client';
import { type TraceParams } from '@apps/web/swr_data_fetching/query_trace_params';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import {
    type ApiResponseType,
    type Mode,
} from '@libraries/typescript/api_client';

const REFERRAL_PROGRAM_ME_PATH =
    '/organizations/{organization_id}/referral-program/me';

export type ReferralProgramMeResponse = ApiResponseType<
    typeof REFERRAL_PROGRAM_ME_PATH,
    'get'
>;

export function useOrganizationUsers(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/users',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.users,
        reason: traceParams.reason,
    });
}

export function useOrganizationMemberProfiles(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/members',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.members,
        reason: traceParams.reason,
    });
}

export function useReferralProgramMe(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: REFERRAL_PROGRAM_ME_PATH,
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

// Normalized offices that exist in the organization, derived from the
// EHR-synced caregiver, client, and shift office fields.
export function useOrganizationOffices(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/offices',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.offices,
        reason: traceParams.reason,
    });
}

// Same endpoint as useOrganizationOffices, but returns the full response so
// callers can also read default_office_ids (the offices to pre-select by
// default, scoped to the org's data source account location_ids). Kept
// separate so existing useOrganizationOffices callers keep their array shape.
export function useOrganizationOfficesWithDefaults(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/offices',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useOrganizationNotifications(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/notifications',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.notifications,
        reason: traceParams.reason,
    });
}

/**
 * Mint a single-use ticket authorizing one notification WebSocket handshake.
 *
 * A WebSocket cannot send an `Authorization` header, and the extension
 * sidepanel renders in a storage-partitioned frame that sends no cookie on the
 * handshake either — so it has no credential it can attach to the socket
 * itself. This call carries the ordinary bearer/cookie credential over HTTP,
 * where it does work, and returns a ticket the socket can present as `?token`.
 *
 * The ticket is single-use and expires in 60s, so callers must mint a fresh
 * one per connection attempt rather than caching it across reconnects.
 */
export async function createOrganizationWebsocketTicket(
    organizationId: string,
) {
    // Deliberately not `idempotent: true`. That is the only way to get the
    // client's own timeout, but it also enables the retry ladder — and issuing
    // is a write, so each attempt mints another live ticket. `useWebSocket`
    // bounds the call instead, which covers every resolver rather than this
    // one.
    return getApiClient().post({
        url: '/organizations/{organization_id}/events/ws-ticket',
        pathParams: { organization_id: organizationId },
    });
}

export function useOrganization(
    organizationId: string | null,
    traceParams: TraceParams,
    mode?: Mode,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        mode,
    });
}
